/**
 * Helper d'injection du tenant override pour les requêtes admin
 * cross-tenant initiées par un super_admin.
 *
 * Le store Pinia `useActiveTenantStore` persiste sa sélection dans
 * `localStorage['p-active-tenant']`. Pour éviter de coupler tous les
 * adapters HTTP à Pinia (et au store dont la résolution dépend du
 * lifecycle Vue), on lit le storage directement ici. C'est lecture
 * pure sans effet de bord.
 *
 * Si le user n'est pas super_admin, le backend ignore le query param
 * et applique son tenant de session — donc on peut injecter sans
 * vérifier le rôle ici (defense in depth côté backend).
 */

const ACTIVE_TENANT_STORAGE_KEY = 'p-active-tenant'

/**
 * readActiveTenantId : retourne le tenant_id override actif (super_admin
 * filtre) ou null si aucun override.
 */
export function readActiveTenantId(): string | null {
    if (typeof window === 'undefined') return null
    const value = window.localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY)
    return value && value.length > 0 ? value : null
}

/**
 * appendActiveTenant : ajoute `tenant_id=xxx` à un URLSearchParams si
 * un override est actif ET que la clé n'est pas déjà fournie.
 *
 * Mutation in-place + retour pour chaînage. Cas d'usage typique :
 *
 *   const params = new URLSearchParams({ limit: '50' })
 *   appendActiveTenant(params)
 *   const url = `/api/v1/apikeys?${params.toString()}`
 */
export function appendActiveTenant(params: URLSearchParams): URLSearchParams {
    if (params.has('tenant_id')) return params
    const tid = readActiveTenantId()
    if (tid) params.set('tenant_id', tid)
    return params
}

/**
 * applyActiveTenantToBody : injecte `tenant_id` dans un body de POST
 * si un override est actif et que le caller ne l'a pas déjà fourni.
 *
 * Utile sur create() de ressources rattachées à un tenant (api-keys,
 * provider-keys, plans, routes, users). Pour les ressources globales
 * (tenants/, providers/ stateless), ne pas appeler ce helper.
 *
 * Retourne un nouvel objet (pas de mutation).
 */
export function applyActiveTenantToBody<T extends Record<string, unknown>>(
    body: T,
): T & { tenant_id?: string } {
    if ('tenant_id' in body && body.tenant_id) return body
    const tid = readActiveTenantId()
    if (!tid) return body
    return { ...body, tenant_id: tid }
}
